import { BucketCard } from './bucket-card';
import { budgetData, Category, transactionData } from './budget-data';
import { BudgetTable } from './budget-table';
import { BudgetTableTotal } from './budget-table-total';
import { TransactionTable } from './transaction-table';

type Props = {};

export function Overview({}: Props) {
  const newBudgetData = moveBucketAmount(budgetData);

  return (
    <div>
      <h1 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl lg:text-6xl/none">
        The Liquid Budget
      </h1>

      <p className="leading-7 [&:not(:first-child)]:mt-6 text-lg">
        Liquid Budget is a digital representation of the cash envelope system. Imagine dumping all of the money you have
        into a big pile on the floor:
      </p>

      <div className="flex justify-center m-12">
        <img src="/money.svg" alt="Money" className="max-w-full h-auto" />
      </div>

      <p className="leading-7 [&:not(:first-child)]:mt-6  text-lg">
        This includes all of your <strong>cash</strong>, <strong>checking</strong>, and{' '}
        <strong>savings accounts</strong>. This pile represents the total amount of your liquid budget, and by itself it
        doesn't tell you much about your financial situation. So let's assign your money to buckets to gain some more
        insight.
      </p>

      <h3 className="mt-16 scroll-m-20 text-2xl font-semibold tracking-tight"> Buckets of Cash</h3>

      <div className="p-5 pt-10 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 items-start">
        {budgetData.flatMap((category) =>
          category.buckets.map((bucket) => <BucketCard key={bucket.id} bucket={bucket} />),
        )}
      </div>
      <p className="leading-7 [&:not(:first-child)]:mt-6 text-lg">
        Now imagine paying for things with the cash you assigned to each bucket.
      </p>

      <p className="leading-7 [&:not(:first-child)]:mt-6 text-lg">
        This is a simple, powerful, and flexible way to manage your money because it forces you to make decisions about
        your spending. For example, if you spend more on <strong>Shopping</strong> than you have in your bucket, you
        need to decide which bucket you can dip into to cover the overdraft.
      </p>

      <h3 className="mt-16 scroll-m-20 text-2xl font-semibold tracking-tight"> Categories</h3>

      <p className="leading-7 [&:not(:first-child)]:mt-6 text-lg">
        Let's keep your buckets organized by grouping them into categories. This helps you focus on your immediate
        spending and at the same time prepare for future expenses.
      </p>

      <div className="mt-8">
        <BudgetTableTotal categories={budgetData} />
      </div>

      <h3 className="mt-16 scroll-m-20 text-2xl font-semibold tracking-tight"> Transactions</h3>

      <p className="leading-7 [&:not(:first-child)]:mt-6 text-lg">
        In a traditional budget, your job would be done until it was time to see how well you did at the end of the
        month. But a passive budget is ineffective because it doesn't enforce any decision making. Instead, we are going
        to update our budget in real-time by adding and categorizing transactions as they happen.
      </p>

      <div className="mt-10">
        <TransactionTable transactions={transactionData} />
      </div>

      <h3 className="mt-16 scroll-m-20 text-2xl font-semibold tracking-tight"> Enhancing our Budget</h3>

      <p className="leading-7 [&:not(:first-child)]:mt-6 text-lg">
        Lets break apart our budget table to capture this activity. We now have the original amount we allocated to our
        buckets in the <strong>Assigned</strong> column, the total outflow from our transactions in the{' '}
        <strong>Outflow</strong> column, and the remaining balance in the <strong>Available</strong> column.
      </p>

      <div className="mt-8">
        <BudgetTable categories={budgetData.filter((category) => category.id !== '3')} />
      </div>

      <h3 className="mt-16 scroll-m-20 text-2xl font-semibold tracking-tight"> Balancing the Budget</h3>

      <p className="leading-7 [&:not(:first-child)]:mt-6 text-lg">
        With that last set of transactions we wiped out our <strong> Mortgage</strong> bucket for the month, reduced our
        balance for <strong>Groceries</strong>, and overdrafted our <strong>Shopping</strong> bucket. But that's ok!
        Let's cover the overdraft with money from another bucket, such as <strong>Dining Out</strong>.
      </p>

      <div className="mt-8">
        <BudgetTable categories={newBudgetData.filter((category) => category.id === '1')} />
      </div>

      <p className="leading-7 [&:not(:first-child)]:mt-6 text-lg ">
        Every time we spend money, we're choosing not to spend it somewhere else - yet most of us make these trade-offs
        without realizing it. We often think budgeting is about restriction, but its real power lies in awareness: when
        we acknowledge that each dollar can only be spent once, we start making decisions that truly matter.
      </p>
    </div>
  );
}

export function moveBucketAmount(originalData: Category[]): Category[] {
  return originalData.map((category) => ({
    ...category,
    buckets: category.buckets.map((bucket) => {
      if (bucket.id === '2') {
        return {
          ...bucket,
          assigned: bucket.assigned - 100,
        };
      }
      if (bucket.id === '3') {
        return {
          ...bucket,
          assigned: bucket.assigned + 100,
        };
      }
      return bucket;
    }),
  }));
}
